import { FC, forwardRef, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import clsx from 'clsx';
import momentTZ from 'moment-timezone';
import { TIMEZONE_GROUPS, ZoneGroup, findZone, currentTimeFor } from './timezoneData';
import { getZipTimezone, getPlaceTimezones, PlaceTimezoneMatch } from '../../../../helpers/api';

// Trigger styled to match the app's react-select controls (Metronic form-control-solid box,
// a thin vertical indicator separator, and a chevron) rather than a default Bootstrap button.
type ToggleControlProps = {
  label: string;
  isPlaceholder?: boolean;
  className?: string;
  disabled?: boolean;
  open?: boolean;
  clearable?: boolean;
  onToggle?: () => void;
  onClear?: () => void;
};

const ToggleControl = forwardRef<HTMLDivElement, ToggleControlProps>(
  ({ label, isPlaceholder, className, disabled, open, clearable, onToggle, onClear }, ref) => (
    <div
      ref={ref}
      role="combobox"
      aria-haspopup="listbox"
      aria-expanded={!!open}
      aria-disabled={disabled || undefined}
      tabIndex={disabled ? -1 : 0}
      onClick={() => !disabled && onToggle?.()}
      onKeyDown={(e) => {
        if (disabled) return;
        if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
          e.preventDefault();
          onToggle?.();
        }
      }}
      className={clsx('form-control form-control-solid d-flex align-items-center text-start', className)}
      style={{ minHeight: '38px', cursor: disabled ? undefined : 'pointer' }}
    >
      <span className={clsx('flex-grow-1 text-truncate', { 'text-muted': isPlaceholder })}>{label}</span>
      {clearable && (
        <button
          type="button"
          aria-label="Reset to local time zone"
          className="btn btn-icon p-0 border-0 bg-transparent d-flex align-items-center"
          style={{ width: 18, height: 18, flexShrink: 0 }}
          onClick={(e) => {
            e.stopPropagation();
            onClear?.();
          }}
        >
          <svg height="16" width="16" viewBox="0 0 20 20" aria-hidden focusable="false" style={{ fill: '#999999' }}>
            <path d="M14.348 14.849c-0.469 0.469-1.229 0.469-1.697 0l-2.651-3.030-2.651 3.029c-0.469 0.469-1.229 0.469-1.697 0-0.469-0.469-0.469-1.228 0-1.697l2.758-3.15-2.759-3.152c-0.469-0.469-0.469-1.228 0-1.697s1.228-0.469 1.697 0l2.652 3.031 2.651-3.031c0.469-0.469 1.228-0.469 1.697 0s0.469 1.229 0 1.697l-2.758 3.152 2.758 3.15c0.469 0.469 0.469 1.229 0 1.698z" />
          </svg>
        </button>
      )}
      <span
        aria-hidden
        style={{ alignSelf: 'stretch', width: 1, backgroundColor: '#cccccc', margin: '0.5rem 0.5rem' }}
      />
      <svg
        height="18"
        width="18"
        viewBox="0 0 20 20"
        aria-hidden
        focusable="false"
        style={{ fill: '#999999', flexShrink: 0 }}
      >
        <path d="M4.516 7.548c0.436-0.446 1.043-0.481 1.576 0l3.908 3.747 3.908-3.747c0.533-0.481 1.141-0.446 1.574 0 0.436 0.445 0.408 1.197 0 1.615-0.406 0.418-4.695 4.502-4.695 4.502-0.217 0.223-0.502 0.335-0.787 0.335s-0.57-0.112-0.789-0.335c0 0-4.287-4.084-4.695-4.502-0.408-0.418-0.436-1.17 0-1.615z" />
      </svg>
    </div>
  )
);

type Props = {
  value: string | undefined;
  onChange?: (newValue: any) => void;
  className?: string;
  disabled?: boolean;
  // Retained for consumer compatibility; not functional in the popover design (WST-764).
  classNamePrefix?: string;
  compact?: boolean;
  showCurrentUserTimezone?: boolean;
};

const ZIP_RE = /^\d{3,}$/;
type Pos = { top: number; left: number; width: number };

const TimezoneSelect: FC<Props> = ({ value, onChange, className, disabled }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [zipMatch, setZipMatch] = useState<string | null>(null);
  const [placeMatches, setPlaceMatches] = useState<PlaceTimezoneMatch[]>([]);
  const [now, setNow] = useState(() => momentTZ());
  const [pos, setPos] = useState<Pos | null>(null);
  const triggerRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const selected = value ? findZone(value) : undefined;
  // Flag (purple border) when the selected zone differs from the browser's local zone, matching the
  // previous selector's cue that you're viewing a non-local timezone.
  const guessTimeZone = useMemo(() => momentTZ.tz.guess(), []);
  const isNonLocal = !!value && value !== guessTimeZone;

  // Position the portaled panel just below the trigger (fixed coords from the trigger's rect).
  const updatePosition = () => {
    const el = triggerRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setPos((prev) => {
      const next = { top: r.bottom + 4, left: r.left, width: Math.max(r.width, 320) };
      if (prev && prev.top === next.top && prev.left === next.left && prev.width === next.width) return prev;
      return next;
    });
  };

  useLayoutEffect(() => {
    if (open) updatePosition();
  }, [open]);

  // While open: refresh per-row times each minute, focus search, and keep the panel anchored to the
  // trigger on ancestor scroll / resize (ignoring scrolls that originate inside the panel's own list).
  useEffect(() => {
    if (!open) {
      setQuery('');
      setZipMatch(null);
      setPlaceMatches([]);
      return;
    }
    inputRef.current?.focus();
    setNow(momentTZ());
    const minute = setInterval(() => setNow(momentTZ()), 60000);

    const onDocMouseDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (panelRef.current?.contains(t) || triggerRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    const onScroll = (e: Event) => {
      if (panelRef.current && e.target instanceof Node && panelRef.current.contains(e.target)) return;
      updatePosition();
    };
    document.addEventListener('mousedown', onDocMouseDown);
    document.addEventListener('keydown', onKeyDown);
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', updatePosition);
    return () => {
      clearInterval(minute);
      document.removeEventListener('mousedown', onDocMouseDown);
      document.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [open]);

  // Zip-aware search: debounce numeric queries to the backend lookup.
  useEffect(() => {
    const q = query.trim();
    if (!ZIP_RE.test(q)) {
      setZipMatch(null);
      return;
    }
    let cancelled = false;
    const t = setTimeout(async () => {
      const tz = await getZipTimezone(q);
      if (!cancelled) setZipMatch(tz);
    }, 350);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [query]);

  // Place-aware search: debounce non-numeric queries (3+ chars) to the backend city lookup.
  // Mutually exclusive with the zip path via ZIP_RE, so the two never race.
  useEffect(() => {
    const q = query.trim();
    if (ZIP_RE.test(q) || q.length < 3) {
      setPlaceMatches([]);
      return;
    }
    let cancelled = false;
    const t = setTimeout(async () => {
      const matches = await getPlaceTimezones(q);
      if (!cancelled) setPlaceMatches(matches);
    }, 350);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [query]);

  const groups: ZoneGroup[] = useMemo(() => {
    const q = query.trim();
    if (ZIP_RE.test(q)) {
      if (!zipMatch) return [];
      const z = findZone(zipMatch);
      return [{ group: z.group, zones: [{ id: z.id, label: z.label }] }];
    }
    // Match the friendly label OR the IANA id, so typing a city (e.g. "tokyo")
    // surfaces a zone even when its curated label omits the city name. An empty
    // needle is a substring of every string, so '' matches all zones.
    const needle = q.toLowerCase();
    const curated = TIMEZONE_GROUPS.map((g) => ({
      group: g.group,
      zones: g.zones.filter(
        (z) => z.label.toLowerCase().includes(needle) || z.id.toLowerCase().includes(needle)
      ),
    })).filter((g) => g.zones.length > 0);
    // Backend city matches lead the list; each city+state is its own row since one
    // name can span several timezones (e.g. Nashville TN vs Nashville IN).
    const locations: ZoneGroup[] = placeMatches.length
      ? [
          {
            group: 'Locations',
            zones: placeMatches.map((m) => ({
              id: m.timezone,
              label: `${m.place_name}, ${m.admin_code1} (${findZone(m.timezone).label})`,
            })),
          },
        ]
      : [];
    return [...locations, ...curated];
  }, [query, zipMatch, placeMatches]);

  const choose = (id: string) => {
    onChange?.(id);
    setOpen(false);
  };

  return (
    <>
      <ToggleControl
        ref={triggerRef}
        className={clsx(className, { 'border border-primary': isNonLocal })}
        disabled={disabled}
        isPlaceholder={!selected}
        label={selected ? selected.label : 'Select timezone...'}
        open={open}
        clearable={isNonLocal}
        onToggle={() => !disabled && setOpen((o) => !o)}
        onClear={() => onChange?.(guessTimeZone)}
      />
      {open &&
        pos &&
        createPortal(
          <div
            ref={panelRef}
            className="bg-body border rounded shadow-sm p-2"
            style={{ position: 'fixed', top: pos.top, left: pos.left, width: pos.width, zIndex: 9999 }}
          >
            <input
              ref={inputRef}
              className="form-control form-control-sm mb-2"
              placeholder="Search..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
            <div
              role="listbox"
              aria-label="Time zone"
              style={{ maxHeight: 300, overflowY: 'auto', overscrollBehavior: 'contain' }}
            >
              {groups.length === 0 && <div className="text-muted small px-2 py-1">No match</div>}
              {groups.map((g) => (
                <div role="group" aria-label={g.group} key={g.group}>
                  <div className="fw-bold text-uppercase text-muted small px-2 pt-2 pb-1">{g.group}</div>
                  {g.zones.map((z) => (
                    <button
                      type="button"
                      role="option"
                      aria-selected={z.id === value}
                      key={`${z.id}|${z.label}`}
                      className={clsx('btn btn-sm w-100 d-flex justify-content-between text-start', {
                        'btn-primary': z.id === value,
                        'btn-link text-body text-decoration-none': z.id !== value,
                      })}
                      onClick={() => choose(z.id)}
                    >
                      <span>{z.label}</span>
                      <span className="text-muted">{currentTimeFor(z.id, now)}</span>
                    </button>
                  ))}
                </div>
              ))}
            </div>
          </div>,
          document.body
        )}
    </>
  );
};

export { TimezoneSelect };
